/* ==========================================================================
   Virsaay — Editorial Navigation
   Desktop: a two-tier sticky bar (logo over an inline nav) that condenses into
   one row on scroll, plus a mega menu that shows each collection's campaign
   frame and pieces inside one viewport. No hamburger on desktop.
   Mobile: 100dvh full-screen editorial index. Scroll locking goes through
   VirsaayMotion.lockScroll; there is no smooth-scroll library in this theme.
   ========================================================================== */

(function () {
  'use strict';

  function waitForMotion(callback) {
    if (window.VirsaayMotion && window.VirsaayMotion.ready) {
      window.VirsaayMotion.ready(callback);
      return;
    }
    var attempts = 0;
    var timer = setInterval(function () {
      attempts++;
      if (window.VirsaayMotion && window.VirsaayMotion.ready) {
        clearInterval(timer);
        window.VirsaayMotion.ready(callback);
      } else if (attempts > 60) {
        clearInterval(timer);
        if (typeof window.gsap !== 'undefined') {
          callback(window.gsap);
        }
      }
    }, 20);
  }

  var desktopQuery = window.matchMedia('(min-width: 990px)');

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function onQueryChange(query, fn) {
    if (query.addEventListener) {
      query.addEventListener('change', fn);
    } else if (query.addListener) {
      query.addListener(fn);
    }
  }

  // Read at call time: GSAP and VirsaayMotion can land after the first boot.
  function motionOff() {
    var Motion = window.VirsaayMotion;
    return (
      !window.gsap ||
      (Motion && Motion.reduced) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function dur(seconds) {
    var Motion = window.VirsaayMotion;
    return Motion && Motion.dur ? Motion.dur(seconds) : seconds;
  }

  function scrollTop() {
    return window.scrollY || window.pageYOffset || 0;
  }

  /* ----------------------------------------------------------------------
     Desktop: the bar condenses on scroll

     The outer header keeps its height; only the painted bar shrinks, so the
     page underneath never jumps. GSAP Flip glides the logo, nav and icons
     from the two-tier layout into the single row (and back).
     ---------------------------------------------------------------------- */

  function initCondense(header) {
    if (header.dataset.sticky === 'false') return;

    var bar = header.querySelector('[data-header-bar]');
    var logo = header.querySelector('[data-flip="logo"]');
    var rest = toArray(header.querySelectorAll('[data-flip]')).filter(function (el) {
      return el !== logo;
    });

    var stuck = scrollTop() > 48;
    var animating = false;
    var queued = false;
    var flipRegistered = false;

    header.classList.toggle('is-stuck', stuck);

    function setStuck(next) {
      stuck = next;
      var gsap = window.gsap;
      var Flip = window.Flip;

      if (!desktopQuery.matches || motionOff() || !Flip || !bar || !logo) {
        header.classList.toggle('is-stuck', next);
        return;
      }

      if (!flipRegistered) {
        gsap.registerPlugin(Flip);
        flipRegistered = true;
      }

      var fromHeight = bar.offsetHeight;
      var logoState = Flip.getState(logo);
      var restState = Flip.getState(rest);

      header.classList.toggle('is-stuck', next);

      var toHeight = bar.offsetHeight;
      var duration = dur(0.7);
      var ease = 'power4.inOut';
      animating = true;

      var tl = gsap.timeline({
        onComplete: function () {
          animating = false;
          evaluate();
        }
      });
      tl.add(Flip.from(logoState, { duration: duration, ease: ease, scale: true }), 0);
      tl.add(Flip.from(restState, { duration: duration, ease: ease }), 0);
      tl.fromTo(
        bar,
        { height: fromHeight },
        { height: toHeight, duration: duration, ease: ease, clearProps: 'height' },
        0
      );
    }

    function evaluate() {
      queued = false;
      // A layout change finishes before the next one starts; onComplete
      // re-checks, so the bar always settles on the right state.
      if (animating) return;
      var y = scrollTop();
      // Hysteresis: condense past 48px, expand only back near the top, so a
      // scroll resting on the threshold cannot flap between the layouts.
      var next = stuck ? y > 8 : y > 48;
      if (next !== stuck) setStuck(next);
    }

    window.addEventListener(
      'scroll',
      function () {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(evaluate);
      },
      { passive: true }
    );
  }

  /* ----------------------------------------------------------------------
     Desktop: mega menu

     Hover (with intent delays) or click opens a panel; moving across the
     nav morphs the open sheet between panels. Each index entry swaps the
     showcase to that collection's campaign tile and pieces.
     ---------------------------------------------------------------------- */

  function initMegaNav(header) {
    var bar = header.querySelector('[data-header-bar]');
    var nav = header.querySelector('[data-vnav]');
    var mega = header.querySelector('[data-mega]');
    var scrim = header.querySelector('[data-mega-scrim]');
    if (!bar || !nav || !mega) return;

    var sheet = mega.querySelector('[data-mega-sheet]');
    var ink = nav.querySelector('[data-vnav-ink]');
    var items = toArray(nav.querySelectorAll('.vnav__item'));
    var triggers = items.filter(function (el) {
      return el.hasAttribute('data-mega-trigger');
    });
    var panels = toArray(mega.querySelectorAll('[data-mega-panel]'));
    var groups = {};
    toArray(mega.querySelectorAll('[data-mega-group]')).forEach(function (group) {
      groups[group.dataset.megaGroup] = group;
    });

    var current = null;
    var currentGroup = null;
    var hovered = null;
    var openedAt = 0;
    var openScroll = 0;
    var sheetTl = null;
    var timers = {};

    function clear(name) {
      window.clearTimeout(timers[name]);
    }

    function later(name, fn, ms) {
      clear(name);
      timers[name] = window.setTimeout(fn, ms);
    }

    function panelFor(name) {
      return panels.filter(function (p) {
        return p.dataset.megaPanel === name;
      })[0] || null;
    }

    function triggerFor(name) {
      return triggers.filter(function (t) {
        return t.dataset.megaTrigger === name;
      })[0] || null;
    }

    function defaultGroupOf(name) {
      var panel = panelFor(name);
      return panel ? groups[panel.dataset.defaultGroup] : null;
    }

    /* Ink: one hairline that glides between nav items */
    function moveInk(item) {
      if (!ink) return;
      if (!item) {
        ink.classList.remove('is-visible');
        return;
      }
      var appearing = !ink.classList.contains('is-visible');
      if (appearing) ink.style.transition = 'none';
      ink.style.width = item.offsetWidth + 'px';
      ink.style.transform = 'translateX(' + item.offsetLeft + 'px) scaleX(var(--ink-scale, 0))';
      if (appearing) {
        void ink.offsetWidth;
        ink.style.transition = '';
      }
      ink.classList.add('is-visible');
    }

    /* Images sit in hidden groups with loading="lazy", so they would only
       start loading at the moment of reveal. Warm a group as soon as the
       pointer heads for it. */
    function warm(group) {
      if (!group || group._virsaayWarm) return;
      group._virsaayWarm = true;
      toArray(group.querySelectorAll('img[loading="lazy"]')).forEach(function (img) {
        img.loading = 'eager';
      });
    }

    function revealGroup(group) {
      if (motionOff()) return;
      var gsap = window.gsap;
      var media = toArray(group.querySelectorAll('[data-mega-media]'));
      var imgs = toArray(group.querySelectorAll('[data-mega-media] img, [data-mega-media] svg'));
      var meta = toArray(group.querySelectorAll('[data-mega-meta]'));
      gsap.killTweensOf(media.concat(imgs, meta));

      // Stagger by card, so a card's two images move together
      var byCard = function (index, el) {
        return Math.max(0, media.indexOf(el.closest('[data-mega-media]'))) * 0.07;
      };

      gsap.fromTo(
        media,
        { clipPath: 'inset(100% 0% 0% 0%)' },
        { clipPath: 'inset(0% 0% 0% 0%)', duration: dur(0.95), ease: 'expo.out', stagger: 0.07, clearProps: 'clipPath' }
      );
      gsap.fromTo(
        imgs,
        { scale: 1.28 },
        { scale: 1, duration: dur(1.4), ease: 'expo.out', stagger: byCard, clearProps: 'transform' }
      );
      gsap.fromTo(
        meta,
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: dur(0.6), ease: 'power3.out', stagger: 0.06, delay: 0.12, clearProps: 'opacity,transform' }
      );
    }

    function showGroup(name, animate) {
      var next = groups[name];
      if (!next || next === currentGroup) return;
      if (currentGroup) currentGroup.hidden = true;
      currentGroup = next;
      warm(next);
      next.hidden = false;
      if (animate) revealGroup(next);
    }

    function markLink(panel, link) {
      if (!panel) return;
      toArray(panel.querySelectorAll('[data-mega-show]')).forEach(function (el) {
        el.classList.toggle('is-active', el === link);
      });
      var list = panel.querySelector('[data-mega-links]');
      if (list) list.classList.toggle('has-active', !!link);
    }

    function focusInto(panel) {
      var first = panel.querySelector('a[href]');
      if (!first) return;
      window.requestAnimationFrame(function () {
        first.focus({ preventScroll: true });
      });
    }

    function open(name, focusFirst) {
      clear('open');
      clear('close');
      var panel = panelFor(name);
      if (!panel) return;
      if (current === name) {
        if (focusFirst) focusInto(panel);
        return;
      }

      var gsap = window.gsap;
      var still = motionOff();
      var wasOpen = current !== null;
      var previous = wasOpen ? panelFor(current) : null;
      var fromHeight = wasOpen ? sheet.offsetHeight : 0;
      var direction = previous && panels.indexOf(panel) < panels.indexOf(previous) ? -1 : 1;

      current = name;
      openedAt = Date.now();
      if (!wasOpen) openScroll = scrollTop();

      triggers.forEach(function (t) {
        var on = t.dataset.megaTrigger === name;
        t.setAttribute('aria-expanded', on ? 'true' : 'false');
        t.classList.toggle('is-open', on);
      });
      panels.forEach(function (p) {
        p.hidden = p !== panel;
      });
      markLink(panel, null);

      mega.hidden = false;
      header.classList.add('is-mega-open');
      moveInk(triggerFor(name));
      showGroup(panel.dataset.defaultGroup, !still);

      if (focusFirst) focusInto(panel);
      if (still) return;

      var reveal = panel.querySelectorAll('[data-mega-reveal]');
      if (sheetTl) sheetTl.kill();
      sheetTl = gsap.timeline();

      if (!wasOpen) {
        // Sheet unrolls from the bar, the index rises line by line
        if (sheet.style.clipPath) {
          sheetTl.to(sheet, { clipPath: 'inset(0% 0% 0% 0%)', duration: dur(0.7), ease: 'expo.out', clearProps: 'clipPath' }, 0);
        } else {
          sheetTl.fromTo(
            sheet,
            { clipPath: 'inset(0% 0% 100% 0%)' },
            { clipPath: 'inset(0% 0% 0% 0%)', duration: dur(0.9), ease: 'expo.out', clearProps: 'clipPath' },
            0
          );
        }
        sheetTl.fromTo(
          reveal,
          { yPercent: 115 },
          { yPercent: 0, duration: dur(0.95), ease: 'expo.out', stagger: 0.028, clearProps: 'transform' },
          0.08
        );
      } else {
        // Already open: morph the sheet height, slide the new index in from
        // the side the pointer is travelling towards
        gsap.set(sheet, { clearProps: 'height' });
        var toHeight = sheet.offsetHeight;
        sheetTl.fromTo(
          sheet,
          { height: fromHeight, clipPath: 'inset(0% 0% 0% 0%)' },
          { height: toHeight, duration: dur(0.6), ease: 'expo.out', clearProps: 'height,clipPath' },
          0
        );
        sheetTl.fromTo(
          reveal,
          { yPercent: 60, x: 24 * direction, opacity: 0 },
          { yPercent: 0, x: 0, opacity: 1, duration: dur(0.7), ease: 'expo.out', stagger: 0.022, clearProps: 'transform,opacity' },
          0
        );
      }
    }

    // `restoreFocus === true` only for Escape from inside the menu; other
    // callers (cart drawer, search, pointer leaving) take focus elsewhere.
    function close(restoreFocus) {
      clear('open');
      clear('close');
      clear('group');
      if (current === null) return;

      var trigger = triggerFor(current);
      current = null;

      triggers.forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
        t.classList.remove('is-open');
      });
      header.classList.remove('is-mega-open');
      moveInk(hovered);

      if (restoreFocus === true && trigger) trigger.focus({ preventScroll: true });

      function finish() {
        sheetTl = null;
        mega.hidden = true;
        panels.forEach(function (p) {
          p.hidden = true;
        });
        if (currentGroup) currentGroup.hidden = true;
        currentGroup = null;
        if (window.gsap) window.gsap.set(sheet, { clearProps: 'clipPath,height' });
      }

      if (sheetTl) sheetTl.kill();
      if (motionOff()) {
        finish();
        return;
      }

      sheetTl = window.gsap.timeline({ onComplete: finish });
      sheetTl.to(sheet, { clipPath: 'inset(0% 0% 100% 0%)', duration: dur(0.55), ease: 'expo.inOut' });
    }

    /* Nav items: hover intent, ink, click and keyboard */
    items.forEach(function (item) {
      item.addEventListener('pointerenter', function (event) {
        if (event.pointerType === 'touch' || !desktopQuery.matches) return;
        hovered = item;
        moveInk(item);
        clear('close');

        if (item.hasAttribute('data-mega-trigger')) {
          var name = item.dataset.megaTrigger;
          warm(defaultGroupOf(name));
          later('open', function () {
            open(name);
          }, current === null ? 110 : 60);
        } else {
          clear('open');
          if (current !== null) {
            later('close', function () {
              close(false);
            }, 160);
          }
        }
      });

      item.addEventListener('pointerleave', function () {
        clear('open');
      });
    });

    nav.addEventListener('pointerleave', function () {
      hovered = null;
      moveInk(current !== null ? triggerFor(current) : null);
    });

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        var name = trigger.dataset.megaTrigger;
        if (current === name) {
          // A hover opened it a moment ago; this click is the same gesture.
          if (Date.now() - openedAt < 450) return;
          close(false);
        } else {
          // detail === 0 is a keyboard activation: move focus into the menu
          open(name, event.detail === 0);
        }
      });

      trigger.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        open(trigger.dataset.megaTrigger, true);
      });
    });

    /* The rest of the bar (logo, consultation link, icons) closes the menu */
    toArray(bar.querySelectorAll('[data-flip="logo"], [data-flip="actions"], .virsaay-header__col--start')).forEach(function (zone) {
      zone.addEventListener('pointerenter', function (event) {
        if (event.pointerType === 'touch' || current === null) return;
        clear('open');
        later('close', function () {
          close(false);
        }, 200);
      });
    });

    bar.addEventListener('pointerenter', function () {
      clear('close');
    });

    bar.addEventListener('pointerleave', function (event) {
      if (event.pointerType === 'touch') return;
      clear('open');
      if (current !== null) {
        later('close', function () {
          close(false);
        }, 240);
      }
    });

    /* Index entries swap the showcase */
    mega.addEventListener('pointerover', function (event) {
      var link = event.target.closest ? event.target.closest('[data-mega-show]') : null;
      if (!link) return;
      markLink(link.closest('[data-mega-panel]'), link);
      warm(groups[link.dataset.megaShow]);
      later('group', function () {
        showGroup(link.dataset.megaShow, true);
      }, 80);
    });

    mega.addEventListener('pointerout', function (event) {
      var link = event.target.closest ? event.target.closest('[data-mega-show]') : null;
      if (!link) return;
      var to = event.relatedTarget;
      if (to && to.closest && to.closest('[data-mega-show]')) return;
      clear('group');
      markLink(link.closest('[data-mega-panel]'), null);
    });

    mega.addEventListener('focusin', function (event) {
      var link = event.target.closest ? event.target.closest('[data-mega-show]') : null;
      if (!link) return;
      markLink(link.closest('[data-mega-panel]'), link);
      showGroup(link.dataset.megaShow, true);
    });

    /* Ways out */
    if (scrim) {
      scrim.addEventListener('click', function () {
        close(false);
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && current !== null) {
        close(header.contains(document.activeElement));
      }
    });

    document.addEventListener('click', function (event) {
      if (current !== null && !bar.contains(event.target)) close(false);
    });

    bar.addEventListener('focusout', function (event) {
      if (current === null || !event.relatedTarget) return;
      if (!bar.contains(event.relatedTarget)) close(false);
    });

    window.addEventListener(
      'scroll',
      function () {
        if (current === null) return;
        if (Math.abs(scrollTop() - openScroll) > 80) close(false);
      },
      { passive: true }
    );

    window.addEventListener('resize', function () {
      if (current !== null) moveInk(triggerFor(current));
    });

    onQueryChange(desktopQuery, function (event) {
      if (!event.matches) close(false);
    });

    header._virsaayDesktopMenu = {
      close: close,
      isOpen: function () {
        return current !== null;
      }
    };
  }

  /* ----------------------------------------------------------------------
     Mobile Immersive Full-Screen Editorial Navigation
     ---------------------------------------------------------------------- */

  /* Mobile tab bar. Only the selected panel is in the document, so the nav is
     a set of short lists rather than one long scroll of the whole catalogue. */
  function initMobileTabs(panel, gsap, Motion) {
    var tablist = panel.querySelector('[data-mobile-tablist]');
    if (!tablist || tablist.dataset.tabsReady === 'true') return;
    tablist.dataset.tabsReady = 'true';

    var tabs = Array.prototype.slice.call(tablist.querySelectorAll('[data-mobile-tab]'));
    var panels = Array.prototype.slice.call(panel.querySelectorAll('[data-mobile-tabpanel]'));
    var indicator = tablist.querySelector('[data-mobile-tab-indicator]');
    var body = panel.querySelector('[data-mobile-nav-scroll]');
    if (!tabs.length || !panels.length) return;

    function moveIndicator(tab) {
      if (!indicator) return;
      indicator.style.width = tab.offsetWidth + 'px';
      indicator.style.transform = 'translateX(' + tab.offsetLeft + 'px)';
    }

    function select(tab, focusTab) {
      var name = tab.dataset.mobileTab;

      tabs.forEach(function (item) {
        var active = item === tab;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        item.tabIndex = active ? 0 : -1;
      });

      panels.forEach(function (item) {
        var active = item.dataset.mobileTabpanel === name;
        item.hidden = !active;
        item.classList.toggle('is-active', active);

        if (!active) return;

        var curMotion = window.VirsaayMotion || Motion;
        var activeGsap = window.gsap || gsap;
        var reduced =
          (curMotion && curMotion.reduced) ||
          (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
        if (reduced || !activeGsap) return;

        var items = item.querySelectorAll('[data-mobile-link]');
        if (!items.length) return;
        activeGsap.fromTo(
          items,
          { opacity: 0, y: 14 },
          {
            opacity: 1,
            y: 0,
            duration: curMotion && curMotion.dur ? curMotion.dur(0.4) : 0.4,
            ease: 'power2.out',
            stagger: 0.03,
            clearProps: 'transform,opacity'
          }
        );
      });

      moveIndicator(tab);
      if (body) body.scrollTop = 0;
      if (focusTab) tab.focus();
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        select(tab, false);
      });

      tab.addEventListener('keydown', function (event) {
        var index = tabs.indexOf(tab);
        var next = null;
        if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
        if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
        if (event.key === 'Home') next = tabs[0];
        if (event.key === 'End') next = tabs[tabs.length - 1];
        if (!next) return;
        event.preventDefault();
        select(next, true);
      });
    });

    /* offsetWidth reads 0 while the nav is still hidden, so place the
       indicator once the panel is actually laid out, and again on resize. */
    panel._virsaayPlaceTabIndicator = function () {
      var active = tablist.querySelector('.is-active') || tabs[0];
      if (active) moveIndicator(active);
    };
    window.addEventListener('resize', panel._virsaayPlaceTabIndicator);
  }

  function initMobileNav(gsap, Motion) {
    var panel = document.querySelector('[data-mobile-nav]');
    if (!panel) return null;

    var opener = document.querySelector('[data-mobile-nav-open]');
    var closers = panel.querySelectorAll('[data-mobile-nav-close]');
    var links = panel.querySelectorAll('[data-mobile-link]');
    var campaign = panel.querySelector('[data-mobile-campaign]');
    var searchTriggers = panel.querySelectorAll('[data-search-open]');

    var isOpen = false;
    var timeline = null;

    initMobileTabs(panel, gsap, Motion);

    function open(e) {
      if (e && e.preventDefault) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (isOpen) return;
      isOpen = true;

      // 1. Remove hidden attribute and add open classes immediately
      panel.removeAttribute('hidden');
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      panel.classList.add('is-open');
      document.body.classList.add('is-mobile-nav-open');
      document.documentElement.classList.add('is-mobile-nav-open');

      if (opener) opener.setAttribute('aria-expanded', 'true');

      // The tab indicator can only be measured once the panel has a layout
      if (typeof panel._virsaayPlaceTabIndicator === 'function') {
        panel._virsaayPlaceTabIndicator();
      }

      var curMotion = window.VirsaayMotion || Motion;
      if (curMotion && typeof curMotion.lockScroll === 'function') {
        curMotion.lockScroll(true);
      } else {
        document.body.style.overflow = 'hidden';
      }

      var activeGsap = window.gsap || gsap;
      var isReduced = (curMotion && curMotion.reduced) || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

      if (!activeGsap || isReduced) {
        panel.style.transform = 'translateY(0%)';
        panel.style.visibility = 'visible';
        panel.style.pointerEvents = 'auto';
        if (curMotion && curMotion.trapFocus) curMotion.trapFocus(panel);
        return;
      }

      if (timeline) timeline.kill();
      // Parked above the screen and paused: the panel's first layout (tabs,
      // reels) can take a few hundred milliseconds on a phone, and a tween
      // already running would spend that time and jump most of the slide.
      activeGsap.set(panel, { clearProps: 'transform' });
      activeGsap.set(panel, { y: '-100%' });
      timeline = activeGsap.timeline({ paused: true });
      var pending = timeline;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (timeline === pending && isOpen) timeline.play();
        });
      });

      // Smooth slide-down reveal
      timeline.fromTo(
        panel,
        { y: '-100%' },
        {
          y: '0%',
          duration: curMotion && curMotion.dur ? curMotion.dur(0.55) : 0.55,
          ease: 'power3.out',
          clearProps: 'transform'
        },
        0
      );

      // Stagger items into view
      if (links.length) {
        timeline.fromTo(
          links,
          { opacity: 0, y: 18 },
          {
            opacity: 1,
            y: 0,
            duration: curMotion && curMotion.dur ? curMotion.dur(0.45) : 0.45,
            ease: 'power2.out',
            stagger: 0.025,
            clearProps: 'transform,opacity'
          },
          0.1
        );
      }

      if (campaign) {
        timeline.fromTo(
          campaign,
          { opacity: 0, y: 18 },
          {
            opacity: 1,
            y: 0,
            duration: curMotion && curMotion.dur ? curMotion.dur(0.45) : 0.45,
            ease: 'power2.out',
            clearProps: 'transform,opacity'
          },
          0.22
        );
      }

      if (curMotion && curMotion.trapFocus) {
        curMotion.trapFocus(panel);
      }
    }

    // `restoreFocus === false` when a link or another overlay takes over, so
    // focus is not thrown back to the burger behind whatever opens next.
    function close(e, restoreFocus) {
      if (e && e.preventDefault) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (!isOpen) return;
      isOpen = false;

      if (opener) opener.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');

      var curMotion = window.VirsaayMotion || Motion;
      var activeGsap = window.gsap || gsap;
      var isReduced = (curMotion && curMotion.reduced) || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

      var finish = function () {
        panel.classList.remove('is-open');
        panel.setAttribute('hidden', '');
        panel.hidden = true;
        document.body.classList.remove('is-mobile-nav-open');
        document.documentElement.classList.remove('is-mobile-nav-open');
        document.body.style.overflow = '';

        if (activeGsap) {
          activeGsap.set(panel, { clearProps: 'all' });
          if (links.length) activeGsap.set(links, { clearProps: 'all' });
          if (campaign) activeGsap.set(campaign, { clearProps: 'all' });
        }

        if (curMotion && typeof curMotion.lockScroll === 'function') {
          curMotion.lockScroll(false);
        }

        if (curMotion && curMotion.releaseFocus) curMotion.releaseFocus(false, panel);
        if (restoreFocus !== false && opener) opener.focus();
      };

      if (!activeGsap || isReduced) {
        finish();
        return;
      }

      if (timeline) timeline.kill();
      timeline = activeGsap.timeline({ onComplete: finish });

      timeline.to(
        panel,
        {
          y: '-100%',
          duration: curMotion && curMotion.dur ? curMotion.dur(0.38) : 0.38,
          ease: 'power3.in'
        },
        0
      );
    }

    // Direct Event Listeners
    if (opener) {
      opener.addEventListener('click', open);
      opener.addEventListener('touchend', function (e) {
        e.preventDefault();
        open(e);
      }, { passive: false });
    }

    Array.prototype.forEach.call(closers, function (btn) {
      btn.addEventListener('click', close);
      btn.addEventListener('touchend', function (e) {
        e.preventDefault();
        close(e);
      }, { passive: false });
    });

    // Close when clicking any link
    Array.prototype.forEach.call(links, function (linkWrap) {
      var a = linkWrap.tagName.toLowerCase() === 'a' ? linkWrap : linkWrap.querySelector('a');
      if (a && a.getAttribute('href') && a.getAttribute('href') !== '#') {
        a.addEventListener('click', function () {
          close(null, false);
        });
      }
    });

    // Close mobile nav when opening search overlay
    Array.prototype.forEach.call(searchTriggers, function (trigger) {
      trigger.addEventListener('click', function () {
        close(null, false);
      });
    });

    // Global escape key listener
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) {
        close();
      }
    });

    // Automatically close when viewport expands to desktop
    var desktopQuery = window.matchMedia('(min-width: 990px)');
    var onResize = function (mq) {
      if (mq.matches && isOpen) close();
    };
    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener('change', onResize);
    } else if (desktopQuery.addListener) {
      desktopQuery.addListener(onResize);
    }

    return {
      open: open,
      close: close,
      isOpen: function () { return isOpen; }
    };
  }

  /* ----------------------------------------------------------------------
     Global Delegated Click & Touch Support (Fail-Safe)
     ---------------------------------------------------------------------- */

  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest && e.target.closest('[data-mobile-nav-open]');
    if (openBtn) {
      e.preventDefault();
      var panel = document.querySelector('[data-mobile-nav]');
      if (panel && !panel.classList.contains('is-open')) {
        if (window._virsaayMobileNav && window._virsaayMobileNav.open) {
          window._virsaayMobileNav.open(e);
        } else {
          panel.removeAttribute('hidden');
          panel.hidden = false;
          panel.setAttribute('aria-hidden', 'false');
          panel.classList.add('is-open');
          document.body.classList.add('is-mobile-nav-open');
          document.documentElement.classList.add('is-mobile-nav-open');
          openBtn.setAttribute('aria-expanded', 'true');
        }
      }
      return;
    }

    var closeBtn = e.target.closest && e.target.closest('[data-mobile-nav-close]');
    if (closeBtn) {
      e.preventDefault();
      var panel = document.querySelector('[data-mobile-nav]');
      if (panel && panel.classList.contains('is-open')) {
        if (window._virsaayMobileNav && window._virsaayMobileNav.close) {
          window._virsaayMobileNav.close(e);
        } else {
          panel.classList.remove('is-open');
          panel.setAttribute('hidden', '');
          panel.hidden = true;
          panel.setAttribute('aria-hidden', 'true');
          document.body.classList.remove('is-mobile-nav-open');
          document.documentElement.classList.remove('is-mobile-nav-open');
          var opener = document.querySelector('[data-mobile-nav-open]');
          if (opener) opener.setAttribute('aria-expanded', 'false');
        }
      }
      return;
    }
  });

  /* ----------------------------------------------------------------------
     Initialization
     ---------------------------------------------------------------------- */

  function initHeaderSystem(gsap, scope) {
    var header = (scope || document).querySelector('[data-header]');
    if (!header) return;
    if (header.dataset.navReady === 'true') {
      // Re-bind mobile nav if needed
      if (!window._virsaayMobileNav) {
        var Motion = window.VirsaayMotion || null;
        window._virsaayMobileNav = initMobileNav(gsap || window.gsap || null, Motion);
      }
      return;
    }
    header.dataset.navReady = 'true';

    var Motion = window.VirsaayMotion || null;
    var activeGsap = gsap || window.gsap || null;

    initCondense(header);
    initMegaNav(header);
    window._virsaayMobileNav = initMobileNav(activeGsap, Motion);
  }

  // Boot immediately if DOM is ready, or on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initHeaderSystem(window.gsap || null, document);
    });
  } else {
    initHeaderSystem(window.gsap || null, document);
  }

  // Motion enhancement once VirsaayMotion and GSAP are ready. This goes
  // through initHeaderSystem so the navReady guard stops listeners binding
  // twice; in practice it is a no-op, since GSAP's script tags precede the
  // theme's and deferred scripts run in document order.
  waitForMotion(function (gsap) {
    initHeaderSystem(gsap || window.gsap || null, document);
  });

  // Shopify Theme Editor dynamic lifecycle hooks
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target.querySelector('[data-header]')) return;
    var activeGsap = window.gsap || null;
    initHeaderSystem(activeGsap, e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (!e.target.querySelector('[data-header]')) return;
    if (window.VirsaayMotion && window.VirsaayMotion.resetScrollLock) {
      window.VirsaayMotion.resetScrollLock();
    }
  });
})();
